// Privacy policy v1.0 (effective 1 September 2026), served until v1.1 takes effect on 1 November 2026
// (src/content/legal/index.ts getGdprDoc). Delete this folder after that date.
import type { LegalDoc } from "../types";

// French (RGPD). Traduction de la politique de référence (v1.0, 1 sept. 2026).
const gdprFr: LegalDoc = {
  title: "Politique de confidentialité et de protection des données (RGPD / GDPR)",
  effectiveDate: "1er septembre 2026",
  version: "1.0",
  intro:
    "Cette politique explique comment Photohawk traite les données personnelles au titre du RGPD (Règlement Général sur la Protection des Données) et de son équivalent européen, le GDPR, ainsi que du UK GDPR. Le RGPD et le GDPR désignent la même réglementation.",
  sections: [
    {
      heading: "1. Qui nous sommes",
      blocks: [
        {
          type: "p",
          text: "Photohawk est une plateforme de photographie d'événements qui permet aux photographes et aux organisateurs de publier, rechercher et vendre des photos d'événements.",
        },
        {
          type: "ul",
          items: [
            "Entité juridique : CLOUDCONCUR LTD (numéro de société 13399913), société immatriculée en Angleterre et au Pays de Galles, opérant sous le nom commercial Photohawk.",
            "Adresse du siège : 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Royaume-Uni.",
            "Délégué à la protection des données (DPO) : Andrew Hammond — contact support@photohawk.com.",
            "Représentant dans l'UE (art. 27 du RGPD) : nous n'avons pas désigné de représentant dans l'UE, au motif que notre traitement des données de résidents de l'UE est occasionnel et ne constitue pas notre activité principale (art. 27, §2). Nous réexaminons régulièrement ce point et désignerons un représentant si notre traitement évolue.",
            "Autorités de contrôle : l'Information Commissioner's Office (ICO) pour le Royaume-Uni, et les autorités de l'UE, dont la CNIL en France, pour les personnes situées dans l'UE.",
          ],
        },
      ],
    },
    {
      heading: "2. Nos deux rôles — responsable de traitement et sous-traitant",
      blocks: [
        {
          type: "p",
          text: "Photohawk s'adresse à deux catégories de personnes, et notre rôle juridique diffère pour chacune :",
        },
        {
          type: "ul",
          items: [
            "Photographes et organisateurs (nos clients). Pour les données de votre compte (nom, e-mail, informations professionnelles, identifiants, facturation et versements), Photohawk est le responsable de traitement.",
            "Participants et acheteurs (vos clients). Pour les données des personnes figurant sur les photos et les achetant, vous (le photographe/l'organisateur) êtes le responsable de traitement et Photohawk est votre sous-traitant — nous traitons ces données selon vos instructions afin d'exécuter les galeries, la recherche et les ventes que vous avez configurées. Un accord de traitement des données (voir §11) régit cette relation.",
          ],
        },
      ],
    },
    {
      heading: "3. Quelles données personnelles nous traitons",
      blocks: [
        { type: "p", text: "Pour les photographes/organisateurs (nous sommes responsables de traitement) :" },
        {
          type: "ul",
          items: [
            "Identité et contact : nom, e-mail, téléphone, raison sociale, adresse.",
            "Compte et sécurité : identifiants de connexion (via AWS), sessions.",
            "Données financières : données d'abonnement/facturation (via Stripe) et coordonnées de versement (via Trolley).",
          ],
        },
        { type: "p", text: "Pour les participants/acheteurs (nous sommes sous-traitant, pour le compte de l'organisateur) :" },
        {
          type: "ul",
          items: [
            "Identité et contact : nom, e-mail, numéro de téléphone, adresse postale/de facturation.",
            "Photographies des participants.",
            "Données biométriques — lorsque la recherche par visage/selfie est activée (voir §4).",
            "Données de commande et de paiement (traitées via nos prestataires de paiement ; nous ne conservons pas les numéros de carte complets).",
            "Statut de consentement marketing, lorsque le participant y consent.",
          ],
        },
      ],
    },
    {
      heading: "4. Recherche par visage et selfie (données biométriques sensibles)",
      blocks: [
        {
          type: "p",
          text: "Lorsqu'un organisateur active la recherche par selfie/visage, Photohawk crée une représentation mathématique des visages afin qu'un participant puisse retrouver ses propres photos.",
        },
        {
          type: "ul",
          items: [
            "Finalité : uniquement pour associer une personne à ses propres photos d'événement.",
            "Lieu : traitées et stockées dans l'Union européenne (AWS, région Irlande).",
            "Conservation : les données biométriques/de visage sont automatiquement supprimées lorsqu'une galerie expire (sous ~60 jours) et lors de la suppression d'une galerie — au premier des deux événements.",
            "Base légale (art. 9 du RGPD) : les données biométriques constituent une catégorie particulière et bénéficient d'une protection renforcée : elles ne servent qu'à permettre aux personnes de retrouver leurs propres photos dans cette galerie. En tant que responsable de traitement des participants, l'administrateur de la galerie décide si la recherche par visage est appropriée pour son événement et autorisée là où il a lieu. Lorsque la loi locale exige le consentement des personnes photographiées, l'administrateur doit l'avoir obtenu, par exemple lors de l'inscription à l'événement ; à défaut, il doit utiliser à la place la recherche par dossard et par heure, qui n'utilise aucune donnée faciale. Photohawk propose une étape de consentement intégrée : le participant est invité à consentir avant que son selfie ne soit utilisé pour la recherche par visage, et peut refuser.",
            "Mineurs : voir §13.",
          ],
        },
      ],
    },
    {
      heading: "5. Pourquoi nous utilisons les données et nos bases légales",
      blocks: [
        {
          type: "ul",
          items: [
            "Pour fournir le service (héberger les galeries, exécuter la recherche, traiter les commandes) : exécution d'un contrat.",
            "Paiements, versements, obligations fiscales et comptables : exécution d'un contrat et respect d'une obligation légale.",
            "Recherche par visage/selfie (art. 9) : le consentement explicite de la personne qui effectue la recherche, avant l'utilisation de son selfie ; pour les personnes photographiées, comme décrit au §4.",
            "E-mails/messages marketing : uniquement avec le consentement du destinataire (opt-in), qui peut être retiré à tout moment.",
            "Cookies de mesure d'audience et publicitaires : uniquement avec votre consentement, donné via notre bandeau cookies (voir §12).",
            "Sécurité, prévention de la fraude et amélioration du service : notre intérêt légitime, mis en balance avec vos droits.",
          ],
        },
      ],
    },
    {
      heading: "6. Où vos données sont stockées et transferts internationaux",
      blocks: [
        {
          type: "ul",
          items: [
            "Localisation des données : les données des participants et des comptes — y compris les photos et les données biométriques de visage — sont hébergées dans l'Union européenne (AWS, Irlande / eu-west-1). Notre service d'identité (AWS) est également dans l'UE.",
            "Transferts internationaux : certains de nos sous-traitants (voir §7) opèrent en dehors de l'UE/du Royaume-Uni (par ex. aux États-Unis). Lorsque des données personnelles sont transférées hors de l'UE/du Royaume-Uni, nous nous appuyons sur une décision d'adéquation lorsqu'elle s'applique, et à défaut sur les clauses contractuelles types (CCT) de la Commission européenne ainsi que sur l'addendum de transfert de données du Royaume-Uni (UK IDTA), avec des garanties supplémentaires si nécessaire.",
          ],
        },
      ],
    },
    {
      heading: "7. Sous-traitants",
      blocks: [
        {
          type: "p",
          text: "Nous faisons appel à un petit nombre de prestataires soigneusement sélectionnés pour fournir le service, chacun étant lié par un accord de traitement des données (et par les CCT/l'addendum UK lorsqu'ils traitent des données hors de l'UE/du Royaume-Uni) :",
        },
        {
          type: "table",
          head: ["Prestataire", "Finalité", "Données concernées"],
          rows: [
            ["Amazon Web Services (AWS)", "Hébergement, stockage, identité, recherche par visage, e-mails transactionnels — région UE", "Toutes les données personnelles, photos, données biométriques"],
            ["Cloudflare (dont R2)", "CDN, domaines personnalisés, stockage d'objets", "Photos, données de requête"],
            ["Stripe", "Paiements par carte et abonnements", "Données de paiement des acheteurs et clients"],
            ["Xendit", "Paiements (Asie du Sud-Est)", "Données de paiement des acheteurs"],
            ["Paystack", "Paiements (Afrique)", "Données de paiement des acheteurs"],
            ["Trolley", "Versements aux photographes", "Identité et coordonnées bancaires du bénéficiaire"],
            ["Postmark", "E-mails transactionnels", "Noms, adresses e-mail"],
            ["Twilio / WhatsApp", "Distribution de photos et notifications", "Noms, numéros de téléphone"],
            ["Google (Google Analytics)", "Mesure d'audience du site (soumise au consentement)", "Données d'usage/d'appareil, identifiants"],
            ["Meta Platforms (Meta Pixel)", "Publicité et mesure de conversion (soumise au consentement)", "Données d'usage, identifiants"],
          ],
        },
        { type: "p", text: "Une liste à jour est disponible sur demande." },
      ],
    },
    {
      heading: "8. Durée de conservation des données",
      blocks: [
        {
          type: "ul",
          items: [
            "Données biométriques / de visage : supprimées sous ~60 jours après l'expiration d'une galerie, et lors de la suppression d'une galerie (voir §4).",
            "Photos et vidéos : la conservation est contrôlée par le titulaire du compte (l'organisateur/le photographe). Le contenu est conservé aussi longtemps que le titulaire du compte le conserve ; les titulaires de compte peuvent supprimer à tout moment photos, vidéos, galeries et fiches de participants, et nous les supprimons alors. Photohawk n'impose pas de durée de vie fixe à ce contenu.",
            "Commandes et documents financiers : conservés 7 ans pour respecter les obligations fiscales et comptables.",
            "Fiches de contact des participants/acheteurs : contrôlées par l'organisateur — conservées tant que l'organisateur les conserve, et supprimées lorsqu'il les supprime ou sur demande d'effacement vérifiée.",
            "Listes marketing : conservées jusqu'au retrait du consentement.",
            "Les journaux d'exploitation/de sécurité sont conservés 30 jours.",
          ],
        },
      ],
    },
    {
      heading: "9. Comment nous protégeons les données",
      blocks: [
        {
          type: "ul",
          items: [
            "En transit : chiffrées via HTTPS/TLS partout.",
            "Au repos : les photos et contenus sont stockés sur AWS et Cloudflare R2, qui chiffrent les données au repos par défaut ; les identifiants sensibles (par ex. jetons de comptes connectés) sont en outre chiffrés avec AWS.",
            "Contrôles d'accès : accès authentifié via AWS ; l'accès du personnel est limité à ce qui est nécessaire pour exploiter le service.",
          ],
        },
      ],
    },
    {
      heading: "10. Vos droits",
      blocks: [
        {
          type: "p",
          text: "Au titre du RGPD/GDPR et du UK GDPR, vous avez le droit d'accéder à vos données, de les rectifier, de les effacer (« droit à l'oubli »), de limiter le traitement ou de vous y opposer, à la portabilité des données, et de retirer votre consentement à tout moment. Vous avez également le droit d'introduire une réclamation auprès d'une autorité de contrôle (l'ICO au Royaume-Uni, ou votre autorité locale telle que la CNIL en France).",
        },
        { type: "p", text: "Comment les exercer :" },
        {
          type: "ul",
          items: [
            "Photographes/organisateurs (nous sommes responsables de traitement) : contactez notre DPO à support@photohawk.com. Nous répondons dans un délai d'un mois.",
            "Participants/acheteurs (l'organisateur est responsable de traitement) : veuillez contacter le photographe ou l'organisateur de votre événement. Les photographes peuvent supprimer les données personnelles d'un participant — y compris ses photos et toute donnée de recherche par visage — depuis Photohawk, et Photohawk exécutera une demande vérifiée pour le compte de l'organisateur.",
          ],
        },
        {
          type: "p",
          text: "La fermeture d'un compte Photohawk met fin à la facturation mais n'efface pas à elle seule les données stockées ; demandez-nous l'effacement si c'est ce dont vous avez besoin.",
        },
      ],
    },
    {
      heading: "11. Accord de traitement des données (DPA)",
      blocks: [
        {
          type: "p",
          text: "Pour les photographes/organisateurs, notre traitement des données de vos participants est régi par un accord de traitement des données au titre de l'art. 28 du RGPD, que nous mettons à disposition sur demande à support@photohawk.com.",
        },
      ],
    },
    {
      heading: "12. Cookies et technologies similaires",
      blocks: [
        {
          type: "ul",
          items: [
            "Les cookies strictement nécessaires (connexion, sécurité et fonctionnalités essentielles) sont toujours actifs ; ils ne requièrent pas de consentement.",
            "Les cookies de mesure d'audience et publicitaires — Google Analytics et le Meta Pixel (voir §7) — ne sont pas essentiels et ne se chargent qu'après votre consentement, donné via notre bandeau cookies. Vous pouvez modifier ou retirer votre choix à tout moment via le bandeau / les paramètres cookies. Le refus laisse le service pleinement utilisable.",
            "Davantage de détails figurent dans notre Politique de cookies distincte.",
          ],
        },
      ],
    },
    {
      heading: "13. Données des mineurs",
      blocks: [
        {
          type: "p",
          text: "Les événements peuvent inclure des mineurs. Décider si la recherche par visage est appropriée pour un événement revient à l'administrateur de la galerie (voir §4) ; lorsque des mineurs peuvent être présents et que la loi locale exige le consentement des personnes photographiées, l'administrateur doit disposer du consentement d'un parent ou tuteur pour eux, ou utiliser à la place la recherche par dossard et par heure. Photohawk ne crée pas sciemment de données biométriques concernant un enfant sans un consentement exigé par la loi, et l'étape de consentement intégrée renforce ce principe.",
        },
      ],
    },
    {
      heading: "14. Violations de données",
      blocks: [
        {
          type: "p",
          text: "En cas de violation de données personnelles, Photohawk notifiera le responsable de traitement concerné dans les meilleurs délais après en avoir pris connaissance, et l'aidera à respecter ses propres obligations de notification aux autorités de contrôle et aux personnes concernées (art. 33–34 du RGPD).",
        },
      ],
    },
    {
      heading: "15. Modifications de cette politique",
      blocks: [
        {
          type: "p",
          text: "Nous pouvons mettre à jour cette politique ; la « Date d'effet » ci-dessus indique la version en vigueur. Les modifications importantes seront communiquées aux titulaires de compte.",
        },
      ],
    },
    {
      heading: "16. Contact",
      blocks: [
        {
          type: "p",
          text: "Délégué à la protection des données — Andrew Hammond. CLOUDCONCUR LTD (numéro de société 13399913), opérant sous le nom Photohawk. E-mail : support@photohawk.com · 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Royaume-Uni.",
        },
      ],
    },
  ],
};

export default gdprFr;
